import { getter } from '../../../CodesThatsGottaGoSomewhereElse';
import { ObjectManager } from '../../../concepts/objects';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { KeyInput } from '../../../platform/input/KeyInput';
import { Keys } from '../../../platform/input/Keys';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { resolve } from '../../Container';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { Phys } from '../../canvas/entities/Phys';
import { RelativeTo } from '../../canvas/entities/Physical';
import { KeyMappings } from '../../canvas/tooling/KeyMappings';
import { ToolBehavior, ToolSettings } from '../../canvas/tooling/ToolBehavior';
import { ToolManager } from '../../canvas/tooling/ToolManager';
import { ToolOverlay } from '../../canvas/tooling/ToolOverlay';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { ActiveSketchInfo, CreateActiveSketchInfo } from './ActiveSketchInfo';
import { marketSetScaledSize } from './MarkerCommands';
import { Marking } from './Marking';
import { Sketch } from './Sketch';
import { SketchStroke } from './SketchStroke';

export class MarkerToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera!: Camera;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(ToolOverlay) private overlay!: ToolOverlay;
    @resolve(ToolManager) private tools!: ToolManager;
    @resolve(SpatialIndex) private spatial!: SpatialIndex;
    @resolve(EditorSession) private session!: EditorSession;

    private color: string = '#ff5722';
    private size: number = 11;
    private pathData: Point[] = [];
    private pathMode = 'natural' as 'natural' | 'straight';
    private latestAt: number = -100;
    private latest = null as Point | null;
    private counter = 0;

    public init(settings: ToolSettings) {
        this.size = settings.size || this.size;
        this.color = settings.color || this.color;
        this.burden(this.overlay.add(this.paintPreview));
    }

    public getKeyMappings = getter(KeyMappings.from(
        ['KEY_1', [marketSetScaledSize, [1]]],
        ['KEY_2', [marketSetScaledSize, [2]]],
        ['KEY_3', [marketSetScaledSize, [3]]],
        ['KEY_4', [marketSetScaledSize, [4]]],
        ['KEY_5', [marketSetScaledSize, [5]]],
        ['KEY_6', [marketSetScaledSize, [6]]],
        ['KEY_7', [marketSetScaledSize, [7]]],
        ['KEY_8', [marketSetScaledSize, [8]]],
        ['KEY_9', [marketSetScaledSize, [9]]],
        ['KEY_0', [marketSetScaledSize, [0]]],
    ));

    public getPreferredCursor(state: string) {
        if (state === 'idle' || state === 'marking') {
            return 'draw';
        }
    }

    public onKeyDown(state: string, ki: KeyInput): string | void {
        if (state === 'idle' && ki.key === Keys.ENTER) {
            this.doneWithSketch();
        }

        this.pathMode = ki.modifiers.shift ? 'straight' : 'natural';
        this.overlay.repaint();
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {
        const { camera } = this;

        if (state !== 'idle' || mi.button !== MouseButtons.Main) {
            return;
        }

        this.counter++;

        const mpt = camera.toModelPoint(mi.position);
        this.appendNext(mpt);

        this.pathMode = mi.modifiers.shift ? 'straight' : 'natural';
        this.overlay.repaint();

        return 'marking';
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        const { camera } = this;

        if (state !== 'marking') {
            return;
        }

        const now = performance.now();
        const d = now - this.latestAt;
        if (d >= 15) {
            this.appendNext(camera.toModelPoint(mi.position));
            this.latestAt = now;
        }

        this.pathMode = mi.modifiers.shift ? 'straight' : 'natural';
        this.overlay.repaint();
    }

    public onMouseUp(state: string, mi: MouseInput): string | void {
        const { camera } = this;

        if (state !== 'marking') {
            return;
        }

        this.pathMode = mi.modifiers.shift ? 'straight' : 'natural';
        this.appendNext(camera.toModelPoint(mi.position));

        this.complete();

        this.pathData = null;
        this.latest = null;
        this.overlay.repaint();

        return 'idle';
    }

    private appendNext(mpt: Point, { replace }: { replace?: boolean } = {}) {

        // Round to 6dp
        mpt = mpt.round(6);

        // If no latest, we must be the first
        if (!this.pathData || !this.latest) {
            this.latest = mpt;
            this.pathData = [this.latest];
        }
        else {
            this.latest = mpt;
            if (replace) {
                this.pathData[this.pathData.length - 1] = mpt;
            }
            else {
                this.pathData.push(mpt);
            }
        }
    }

    private complete() {
        const { pathData } = this;

        if (!pathData || pathData.length < 2) {
            return; // Not enough data to form a marking
        }

        return (
            (this.completeViaSketchMerge()) ||
            (this.completeViaGraffiti()) ||
            (this.completeViaSketchCreate())
        );
    }

    private completeViaSketchMerge() {

        const { pathData, objects } = this;

        if (!pathData || pathData.length < 2) {
            return false; // Not enough data to form a marking
        }

        const sketch = this.tryGetActiveSketch();
        if (!sketch) return false;

        objects.update(u => {
            u.rolling(`sketching:${sketch.id}:${this.counter}`);
            const stroke = u.create(SketchStroke);
            stroke.color = this.color;
            stroke.thickness = this.size;
            stroke.spine = this.normalizePathData(pathData);
            sketch.append(stroke);
        });

        return true;
    }

    private completeViaSketchCreate() {
        const { pathData, objects } = this;

        if (!pathData || pathData.length < 2) {
            return false; // Not enough data to form a marking
        }

        const pathBounds = Rect.fromPoints(pathData);

        objects.update(u => {
            const sketch = u.create(Sketch);
            sketch.x = pathBounds.left;
            sketch.y = pathBounds.top;
            sketch.width = pathBounds.width;
            sketch.height = pathBounds.height;
            objects.getRoot().add(sketch);
            u.rolling(`sketching:${sketch.id}:${this.counter}`);
            this.session.update(ActiveSketchInfo, CreateActiveSketchInfo(sketch.id));
        });

        return this.completeViaSketchMerge();
    }

    private completeViaGraffiti() {
        const { pathData, objects, spatial } = this;

        // Find the first point
        const first = pathData[0];

        const subject = Phys.from(spatial.lookup(first, GraffitiTarget)).highest();
        if (!subject) return false;

        // 80% of points must be inside the graffiti target bounds to be a valid graffiti stroke
        const threshold = .8;

        if (this.isGraffitiStrokeValid(subject, threshold)) {

            // Convert path data to relative data
            const relativePathData = pathData.map(pt => {
                // Convert to model point to local point relative to center
                const rpt = subject.transform(RelativeTo.Model)
                    .translate(subject.size().multiply(0.5))
                    .inverse()
                    .apply(pt);
                return rpt;
            });

            const normalizedPathData = this.normalizePathData(relativePathData);

            objects.update(u => {
                const nr = u.create(Marking);
                nr.origin = 'center';
                nr.color = this.color;
                nr.size = this.size;
                nr.value = JSON.stringify(Marking.formatValue(normalizedPathData));
                subject.add(nr);
            });

            return true;

        }
        else {
            return false;
        }
    }

    private isGraffitiStrokeValid(subject: GraffitiTarget, threshold: number) {
        // % of strokes inside the graffiti target must be greater than the threshold to be a valid graffiti stroke
        let inside = 0;
        const gtb = subject.bounds(RelativeTo.Model);
        for (const pt of this.pathData) {
            if (gtb.contains(pt)) {
                inside++;
            }
        }
        return inside / this.pathData.length >= threshold;
    }

    private normalizePathData(pathData: Point[]) {
        if (this.pathMode === 'natural') {
            return pathData;
        }
        else {
            return [pathData[0], pathData[pathData.length - 1]];
        }
    }

    private paintPreview = (gfx: UniversalSurfaceRenderer) => {
        if (!this.pathData || !this.latest) return;
        const data = this.pathData.concat(this.latest);
        if (data.length < 2) return;

        const { path, style, size } = Marking.toStrokePath(this.normalizePathData(data).flatMap(p => [p.x, p.y]), this.size);
        if (!path) return;

        const mv = this.camera.modelViewMatrix; // Model to view
        // const om = this.subject.transform(RelativeTo.Model).translate(this.subject.size().multiply(0.5)); // Origin to model

        gfx.setTransform(mv);

        if (style === 'fill') {
            gfx.fillStyle = this.color;
            gfx.fill(new Path2D(path));
        }
        else {
            gfx.strokeStyle = this.color;
            gfx.lineWidth = size;
            gfx.lineCap = 'round';
            gfx.stroke(new Path2D(path));
        }

        // const clip = this.subject.path(RelativeTo.Model).transform(mv).toPath2D();
        gfx.resetTransform();
        gfx.globalCompositeOperation = 'destination-in';
        gfx.fillStyle = '#000';
        gfx.fill();
    };

    private doneWithSketch() {
        this.session.update(ActiveSketchInfo, ActiveSketchInfo.empty);
    }

    private tryGetActiveSketch() {
        const { sketchId } = this.session.get(ActiveSketchInfo);
        return this.objects.getMaybe(sketchId, Sketch);
    }
}
